const MIXPANEL_POPULAR_CAROUSEL_ITEMS_STORAGE_KEY = 'mixpanel:popular-carousel-items'
const MIXPANEL_POPULAR_CAROUSEL_SUMMARY_STORAGE_KEY = 'mixpanel:popular-carousel-summary'

const isBrowser = () => typeof window !== 'undefined'

export type PopularCarouselSummary = {
  containsPopularCarouselItem: boolean
  popularCarouselItemCount: number
  popularCarouselItemQuantity: number
}

type AttributableBasketItem = {
  qty: number
  item?: { _id: string } | null
}

const emptySummary = (): PopularCarouselSummary => ({
  containsPopularCarouselItem: false,
  popularCarouselItemCount: 0,
  popularCarouselItemQuantity: 0,
})

export const getPopularCarouselItemIds = (): string[] => {
  if (!isBrowser()) return []

  const stored = window.sessionStorage.getItem(MIXPANEL_POPULAR_CAROUSEL_ITEMS_STORAGE_KEY)
  if (!stored) return []

  try {
    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((itemId): itemId is string => typeof itemId === 'string')
  } catch {
    return []
  }
}

export const recordPopularCarouselItem = (itemId: string) => {
  if (!isBrowser() || !itemId) return

  const itemIds = getPopularCarouselItemIds()
  if (itemIds.includes(itemId)) return

  window.sessionStorage.setItem(
    MIXPANEL_POPULAR_CAROUSEL_ITEMS_STORAGE_KEY,
    JSON.stringify([...itemIds, itemId]),
  )
}

/**
 * Reconciles against the final basket rather than a flag latched at add time, so
 * an item added from the carousel and then removed stops counting.
 */
export const summarisePopularCarouselItems = (
  basketItems: AttributableBasketItem[],
  attributedItemIds: string[],
): PopularCarouselSummary => {
  const attributed = new Set(attributedItemIds)
  const carouselItems = basketItems.filter(({ item }) => !!item && attributed.has(item._id))

  return {
    containsPopularCarouselItem: carouselItems.length > 0,
    popularCarouselItemCount: carouselItems.length,
    popularCarouselItemQuantity: carouselItems.reduce((total, { qty }) => total + qty, 0),
  }
}

export const setPopularCarouselSummary = (summary: PopularCarouselSummary) => {
  if (!isBrowser()) return
  window.sessionStorage.setItem(
    MIXPANEL_POPULAR_CAROUSEL_SUMMARY_STORAGE_KEY,
    JSON.stringify(summary),
  )
}

/** Reads the summary stored at checkout and clears the session's attribution. */
export const takePopularCarouselSummary = (): PopularCarouselSummary => {
  if (!isBrowser()) return emptySummary()

  const stored = window.sessionStorage.getItem(MIXPANEL_POPULAR_CAROUSEL_SUMMARY_STORAGE_KEY)
  window.sessionStorage.removeItem(MIXPANEL_POPULAR_CAROUSEL_SUMMARY_STORAGE_KEY)
  window.sessionStorage.removeItem(MIXPANEL_POPULAR_CAROUSEL_ITEMS_STORAGE_KEY)
  if (!stored) return emptySummary()

  try {
    const parsed = JSON.parse(stored) as PopularCarouselSummary
    return {
      containsPopularCarouselItem: !!parsed.containsPopularCarouselItem,
      popularCarouselItemCount: parsed.popularCarouselItemCount ?? 0,
      popularCarouselItemQuantity: parsed.popularCarouselItemQuantity ?? 0,
    }
  } catch {
    return emptySummary()
  }
}
